import dayjs from "dayjs";

const contractPageBreakBeforeStyle = {
  breakBefore: "page",
  pageBreakBefore: "always",
} as const;

export default function ContractPreview({ client, form, termMode }: { client: any, form: any, termMode?: "without_trial" | "with_trial" }) {
  if (!client) return null;

  const ailahUEN = "202520802C";
  const ailahAddress = "32 Pekin Street, #05-01, Singapore 048762";

  const contractDate = form.contract_start_date
    ? dayjs(form.contract_start_date).format("D MMMM YYYY")
    : "[Start Date]";

  const signedDate = form.contract_signed_date
    ? dayjs(form.contract_signed_date).format("D MMMM YYYY")
    : "[Signature Date]";

  const includedMessages =
    form.included_messages === null || form.included_messages === undefined || form.included_messages === ""
      ? "[included_messages]"
      : Number(form.included_messages).toLocaleString("en-US");

  const trialDuration =
    form.trial_duration === null || form.trial_duration === undefined || form.trial_duration === ""
      ? "[Trial duration]"
      : Number(form.trial_duration);

  return (
    <div className="contract-pdf no-border-print p-8 pt-4 bg-white text-sm leading-relaxed text-gray-800">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold uppercase">Service Agreement</h2>
        <p className="text-gray-600">This agreement is governed by the laws of Singapore</p>
      </div>

      <h3 className="font-semibold text-gray-700 mb-1">1. Parties to the Agreement</h3>
      <p className="mb-4">
        This Service Agreement ("Agreement") is entered into on <strong>{contractDate}</strong>, by and between
        <strong> Apex Health Systems Pte. Ltd.</strong>, a company incorporated in Singapore under UEN <strong>{ailahUEN}</strong>, whose registered office is at <strong>{ailahAddress}</strong> ("Service Provider"), and
        <strong> {client.raison_sociale}</strong>, a company incorporated in Singapore under UEN <strong>{form.uen || "[Client UEN]"}</strong>, whose registered office is at <strong>{form.registered_address || "[Client Address]"}</strong> ("Client").<br />
        The "final customer" is the Client's customer.
      </p>

      <h3 className="font-semibold text-gray-700 mb-1">2. Purpose</h3>
      <p className="mb-4">
        The Service Provider provides an intelligent assistant service via WhatsApp, including automated response functionality, calendar management, access to the clinic’s knowledge base, and a comprehensive back-office.
      </p>

      <h3 className="font-semibold text-gray-700 mb-1">3. Term</h3>
      <p className="mb-4">
        {termMode === "with_trial" ? (
          <>
            The term of this Agreement shall commence on <strong>{contractDate}</strong> with a trial period of <strong>{trialDuration}</strong> month(s). Following the trial period, this Agreement shall continue on a month-to-month basis, with no minimum commitment period. Either Party may terminate this Agreement by written notice, with such termination taking effect at the end of the then-current calendar month.
          </>
        ) : (
          <>
            The term of this Agreement shall commence on <strong>{contractDate}</strong> and shall continue on a month-to-month basis, with no minimum commitment period. Either Party may terminate this Agreement by written notice, with such termination taking effect at the end of the then-current calendar month.
          </>
        )}
      </p>

      <h3 className="font-semibold text-gray-700 mb-1">4. Scope of Services</h3>
      <ul className="list-disc list-inside ml-4 mb-4">
        <li>Deployment of a personalized WhatsApp chatbot</li>
        <li>Provision of a dedicated phone number</li>
        <li>Integration with or linkage to a calendar</li>
        <li>Access to a secure back-office dashboard</li>
        <li>Management of patient conversations</li>
        <li>Access to a documentary knowledge base</li>
        <li>Push notifications via the web application</li>
        <li>Technical assistance</li>
      </ul>

      <h3 className="font-semibold text-gray-700 mb-1">5. Client Obligations, Usage Limits and Fair Use Policy</h3>
      <p className="mb-4">
        The Client shall provide all necessary information and cooperation for the setup and operation of the Services. The Client is permitted up to {includedMessages} messages per month. Messages in excess of this threshold shall be billed at SGD <strong>{form.additional_message_fee_sgd}</strong> per additional message.
        <br /><br />
        The Client agrees to comply with a Fair Usage Policy: the Services must not be misused, including but not limited to spamming, automation abuse, or any unlawful purpose. The Service Provider may suspend Services if such misuse is suspected, subject to one (1) prior written warning and a grace period of thirty (30) days to remedy the issue.
      </p>

      <h3 className="font-semibold text-gray-700 mb-1">6. Fees and Payment Terms</h3>
      <p className="mb-4">
        • Setup Fee: <strong>SGD {form.setup_fee_sgd}</strong><br />
        • Monthly Subscription: <strong>SGD {form.base_subscription_sgd}</strong><br />
        • Additional Message Fee: <strong>SGD {form.additional_message_fee_sgd}</strong> per excess message.<br />
        • Template Message Fee: <strong>SGD {form.template_message_fee_sgd}</strong> per template message sent.<br /><br />
        The Monthly Subscription is charged on a month-to-month basis with no long-term commitment.<br /><br />
        All payments are non-refundable. Invoices are payable within thirty (30) days of issuance. Late payments shall accrue interest at one percent (1%) per month, or the maximum permitted by law. Services may be suspended if payment remains outstanding after one written notice and a thirty (30) day cure period.
      </p>

      <h3 className="font-semibold text-gray-700 mb-1">7. Confidentiality</h3>
      <p className="mb-4">
        All confidential information, exchanged through the provide service, in the chats, shall not be disclosed to third parties without prior written consent, except as required by law. This obligation survives termination.
      </p>

      <h3 className="font-semibold text-gray-700 mb-1">8. Intellectual Property</h3>
      <p className="mb-4">
        All rights in the Services remain the exclusive property of the Service Provider. The Client is granted a limited, non-exclusive, non-transferable right to use the Services during the Term. No intellectual property rights are assigned to the Client.
      </p>

      <h3 className="font-semibold text-gray-700 mb-1">9. Data Protection and Medical Disclaimer</h3>
      <p className="mb-4">
        Both parties shall comply with Singapore’s Personal Data Protection Act (“PDPA”). The Service Provider processes final customers’ personal data on behalf of and for the purposes of the Client, as a data intermediary within the meaning of the PDPA, pursuant to this written Agreement. The Client remains responsible for the consent and notification obligations under the PDPA towards its final customers.<br /><br />
        {form.pdpa_notice_optout && (
          <>
            <strong>PDPA notice opt-out.</strong> At the Client’s request, the automatic PDPA data-storage notices normally sent by the Service Provider to final customers are disabled. The Client represents and warrants that it informs its final customers, by its own means, of the collection, use and storage of their personal data, and assumes sole responsibility for compliance with the PDPA notification and consent obligations in respect of such data. The Client shall indemnify the Service Provider against any claim, penalty or expense arising from a breach of these obligations.<br /><br />
          </>
        )}
        The Services are provided on an ‘as-is’ basis, without warranty as to fitness for purpose or quality.<br /><br />
        The Services are not in any way a medical device or tool, nor are they intended to be or constitute medical advice of any kind. The final customer and the Client remain solely responsible for any medical advice or clinical decision or service.
      </p>

      <section style={contractPageBreakBeforeStyle}>
        <h3 className="font-semibold text-gray-700 mb-1">10. Indemnification</h3>
        <p className="mb-4">
          The Client shall indemnify and hold harmless the Service Provider from any claims, damages, or expenses (including legal fees) arising from (i) misuse of the Services, (ii) content provided by the Client, (iii) breach of this Agreement, (iv) a dispute or disagreement between the Client and any of its final customers; or (v) arising from any breach of law or regulation involving the Client.
        </p>
      </section>

      <h3 className="font-semibold text-gray-700 mb-1">11. Limitation of Liability and Force Majeure</h3>
      <p className="mb-4">
        To the fullest extent permitted by law, the Service Provider's liability is limited to the fees paid in the twelve (12) months preceding the claim. The Service Provider shall not be liable for indirect or consequential damages, including loss of patients, reputation, profits, or data.
        <br /><br />
        For the avoidance of doubt, any fees, charges, or costs imposed by Meta in connection with the Client's use of WhatsApp templates are billed directly by Meta to the Client in accordance with Meta's applicable terms and pricing. The Service Provider is not responsible or liable for Meta's billing, for any changes to Meta's pricing or policies, or for the Client's use, selection, approval status, or deployment of such templates.
        <br /><br />
        Force Majeure includes events beyond reasonable control, including outages of third-party providers (WhatsApp, Twilio, Google, Supabase, OpenAI, etc.). The Service Provider commits to a minimum target of 98% of annual availability.
      </p>

      <h3 className="font-semibold text-gray-700 mb-1">12. Suspension, Termination and Compliance</h3>
      <p className="mb-4">
        Either Party may terminate this Agreement by written notice, with such termination taking effect at the end of the then-current calendar month. Immediate termination is permitted if (i) there is unlawful or non-compliant use, or (ii) a material breach not remedied within fourteen (14) days of written notice. Termination does not relieve the Client from paying any fees accrued or invoiced up to the effective date of termination.
      </p>

      <h3 className="font-semibold text-gray-700 mb-1">13. Governing Law and Dispute Resolution</h3>
      <p className="mb-4">
        This Agreement shall be governed by Singapore law.
      </p>
      <p className="mb-4">
        Any dispute between the Parties which cannot be resolved amicably within 30 days of notice of dispute by one Party to the other shall be submitted for mediation at the Singapore Mediation Centre (SMC) in accordance with SMC’s Mediation Procedure for the time being in force. Either Party may submit a request to mediate to SMC upon which the other Party will be bound to participate in the mediation within [30] days thereof. The Parties shall each be represented by a senior executive with authority to negotiate and settle the dispute. Unless otherwise agreed, the mediator will be appointed by SMC. The mediation will take place in Singapore in the English language.
      </p>
      <p className="mb-4">
        <strong>Either:</strong> The Parties agree to be bound by any settlement agreement reached.
      </p>
      <p className="mb-4">
        <strong>Or:</strong> If the dispute cannot be resolved through mediation within 90 days or otherwise agreed between the Parties after commencement of mediation at the SMC, the Parties shall submit the dispute to arbitration at the Singapore International Arbitration Centre according to the SIAC rules for the time being in force.
      </p>

      <section style={contractPageBreakBeforeStyle}>
        <h3 className="font-semibold text-gray-700 mb-1">14. Boilerplate Clauses</h3>
        <p className="mb-4">
          <strong>No Waiver: </strong> Failure to enforce a right or term under the Agreement does not constitute a waiver of any such right or term.<br />
          <strong>Assignment:</strong> Neither Party may assign the agreement without the written consent of the other Party.<br />
          <strong>Entire Agreement:</strong> This Agreement supersedes all prior representations and understandings between the Parties, whether verbal or written.<br />
          <strong>Amendments:</strong> Must be in writing and signed.<br />
          <strong>Survival:</strong> Clauses on confidentiality, payments, indemnification, IP, disclaimers, and limitation of liability survive termination.
        </p>
      </section>

      <h3 className="font-semibold text-gray-700 mb-1">15. Execution</h3>
      <p className="mb-4">Executed in duplicate, each deemed original:</p>

      <div className="grid grid-cols-2 gap-8 mt-6">
        <div>
          <p className="font-semibold">For Apex Health Systems Pte. Ltd.</p>
          <p>Name: Agastya Samat</p>
          <p>Title: Director</p>
          <div className="mt-8 border-t border-gray-400 w-48" />
          {form.ailah_signed && (
            <div className="text-xs text-green-600 mt-2">
              ✅ Signed by Apex Health Systems Pte. Ltd. on{" "}
              {form.contract_generated_at ? dayjs(form.contract_generated_at).format("D MMM YYYY") : "[Date]"}
              <br />
              Hash: {form.contract_hash || "[hash]"}
            </div>
          )}
        </div>
        <div>
          <p className="font-semibold">For {client.raison_sociale}</p>
          <p>Name: {form.client_signatory_name || "____________________"}</p>
          <p>Title: {form.client_signatory_title || "____________________"}</p>
          <div className="mt-8 border-t border-gray-400 w-48" />
          {form.client_signed && (
            <div className="text-xs text-blue-600 mt-2">
              ✅ Signed by {form.client_signatory_name || client.raison_sociale} on{" "}
              {form.client_signed_date ? dayjs(form.client_signed_date).format("D MMM YYYY") : "[Date]"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
